import { HttpClient } from '../api/client'
import { TokenManager } from '../auth/tokenManager'
import type { ApiResponse, SuggestionStatus, AISuggestionRecord } from '@bandos/shared'

// Re-export types for backward compatibility
export type { SuggestionStatus, AISuggestionRecord }

interface SuggestionsResponse {
  success: boolean
  suggestions: AISuggestionRecord[]
}

interface SuggestionCountsResponse {
  success: boolean
  counts: Record<string, number>
}

interface UpdateStatusResponse {
  success: boolean
  message: string
}

export class SuggestionsService {
  /**
   * Get all pending suggestions for a mindmap
   */
  static async getPendingSuggestions(mindmapId: string): Promise<ApiResponse<SuggestionsResponse>> {
    const headers = await TokenManager.getAuthHeaders()

    if (!headers.Authorization) {
      return {
        data: null,
        error: {
          message: 'Authentication required. Please log in.',
          code: 'AUTH_REQUIRED',
        },
      }
    }

    return HttpClient.get<SuggestionsResponse>(`/suggestions/${mindmapId}`, headers)
  }

  /**
   * Get pending suggestions for a specific source node
   */
  static async getSuggestionsForNode(
    mindmapId: string,
    nodeId: string
  ): Promise<ApiResponse<SuggestionsResponse>> {
    const headers = await TokenManager.getAuthHeaders()

    if (!headers.Authorization) {
      return {
        data: null,
        error: {
          message: 'Authentication required. Please log in.',
          code: 'AUTH_REQUIRED',
        },
      }
    }

    return HttpClient.get<SuggestionsResponse>(
      `/suggestions/${mindmapId}/node/${encodeURIComponent(nodeId)}`,
      headers
    )
  }

  /**
   * Get suggestion counts per source node for a mindmap
   */
  static async getSuggestionCounts(
    mindmapId: string
  ): Promise<ApiResponse<SuggestionCountsResponse>> {
    const headers = await TokenManager.getAuthHeaders()

    if (!headers.Authorization) {
      return {
        data: null,
        error: {
          message: 'Authentication required. Please log in.',
          code: 'AUTH_REQUIRED',
        },
      }
    }

    return HttpClient.get<SuggestionCountsResponse>(`/suggestions/${mindmapId}/counts`, headers)
  }

  /**
   * Accept a suggestion (mark as accepted)
   */
  static async acceptSuggestion(suggestionId: string): Promise<ApiResponse<UpdateStatusResponse>> {
    const headers = await TokenManager.getAuthHeaders()

    if (!headers.Authorization) {
      return {
        data: null,
        error: {
          message: 'Authentication required. Please log in.',
          code: 'AUTH_REQUIRED',
        },
      }
    }

    return HttpClient.patch<UpdateStatusResponse>(
      `/suggestions/${suggestionId}/status`,
      { status: 'accepted' },
      headers
    )
  }

  /**
   * Dismiss a suggestion (mark as dismissed)
   */
  static async dismissSuggestion(suggestionId: string): Promise<ApiResponse<UpdateStatusResponse>> {
    const headers = await TokenManager.getAuthHeaders()

    if (!headers.Authorization) {
      return {
        data: null,
        error: {
          message: 'Authentication required. Please log in.',
          code: 'AUTH_REQUIRED',
        },
      }
    }

    return HttpClient.patch<UpdateStatusResponse>(
      `/suggestions/${suggestionId}/status`,
      { status: 'dismissed' },
      headers
    )
  }

  /**
   * Dismiss all pending suggestions for a source node
   */
  static async dismissAllForNode(
    mindmapId: string,
    nodeId: string
  ): Promise<ApiResponse<UpdateStatusResponse>> {
    const headers = await TokenManager.getAuthHeaders()

    if (!headers.Authorization) {
      return {
        data: null,
        error: {
          message: 'Authentication required. Please log in.',
          code: 'AUTH_REQUIRED',
        },
      }
    }

    return HttpClient.post<UpdateStatusResponse>(
      `/suggestions/${mindmapId}/node/${encodeURIComponent(nodeId)}/dismiss-all`,
      {},
      headers
    )
  }
}
